import { FormEvent, FunctionComponent, useEffect, useRef } from 'react';
import View from './view';

const Container: FunctionComponent = () => {
  const scrollytellingElementRef = useRef<HTMLElement>(null);
  const loginTextElementRef = useRef<HTMLSpanElement>(null);
  const discoverTextElementRef = useRef<HTMLSpanElement>(null);
  const matchesTextElementRef = useRef<HTMLSpanElement>(null);
  const downloadTextElementRef = useRef<HTMLSpanElement>(null);
  const mockupVideoElementRef = useRef<HTMLVideoElement>(null);
  const mockupImageElementRef = useRef<HTMLImageElement>(null);
  const emailNotifyFormElementRef = useRef<HTMLFormElement>(null);
  const emailInputElementRef = useRef<HTMLInputElement>(null);
  const formResponseElementRef = useRef<HTMLSpanElement>(null);

  // Add animations to scrollytelling section
  useEffect(() => {
    // Get DOM elements
    const scrollytellingElement = scrollytellingElementRef.current;
    const loginTextElement = loginTextElementRef.current;
    const discoverTextElement = discoverTextElementRef.current;
    const matchesTextElement = matchesTextElementRef.current;
    const downloadTextElement = downloadTextElementRef.current;
    const mockupVideoElement = mockupVideoElementRef.current;
    const mockupImageElement = mockupImageElementRef.current;
    if (
      scrollytellingElement &&
      loginTextElement &&
      discoverTextElement &&
      matchesTextElement &&
      downloadTextElement &&
      mockupVideoElement &&
      mockupImageElement
    ) {
      let videoDuration = 0;

      // Set constant values
      const playbackSpeed = 1000;
      const loginTime = 2;
      const discoverTime = 4;
      const matchesTime = 7.3;
      const downloadTime = 12;

      // Set height of scrollytelling element
      const handleScrollHeight = (event: Event) => {
        if (event.type === 'loadedmetadata') {
          videoDuration = mockupVideoElement.duration;
        }
        if (
          window.innerWidth > 768 &&
          !window.navigator.userAgent.includes('Firefox')
        ) {
          const scrollHeight = Math.floor(videoDuration + 5) * playbackSpeed;
          scrollytellingElement.style.height = `${scrollHeight}rem`;
          mockupVideoElement.removeAttribute('autoplay');
          mockupVideoElement.removeAttribute('loop');
          mockupVideoElement.playbackRate = 1;
          mockupVideoElement.pause();
        } else {
          scrollytellingElement.style.height = 'auto';
          mockupVideoElement.setAttribute('autoplay', '');
          mockupVideoElement.setAttribute('loop', '');
          mockupVideoElement.playbackRate = 0.5;
          mockupVideoElement.play();
          loginTextElement.style.opacity = '0';
          discoverTextElement.style.opacity = '0';
          matchesTextElement.style.opacity = '0';
          downloadTextElement.style.opacity = '0';
        }
      };
      mockupVideoElement.addEventListener('loadedmetadata', handleScrollHeight);
      window.addEventListener('resize', handleScrollHeight);

      // Add animations when scrolling
      const handleAnimations = () => {
        const rect = scrollytellingElement.getBoundingClientRect();
        if (
          window.innerWidth > 768 &&
          !window.navigator.userAgent.includes('Firefox') &&
          rect.top < 0 &&
          rect.bottom - window.innerHeight > 0
        ) {
          // Calculate animation values
          const axis = window.innerWidth > 768 ? 'X' : 'Y';
          const currentTime = -rect.top / playbackSpeed;
          const textOpacity = Math.max(
            0,
            Math.min(1, currentTime, videoDuration - currentTime)
          );
          const loginTextPos = (currentTime - loginTime) * playbackSpeed;
          const discoverTextPos = (currentTime - discoverTime) * playbackSpeed;
          const matchesTextPos = (currentTime - matchesTime) * playbackSpeed;
          const downloadTextOpacity = Math.max(
            0,
            Math.min(1, -(currentTime - downloadTime - 0.7))
          );
          const downloadTextScale = Math.max(0, currentTime - downloadTime + 1);
          const mockupOpacity = Math.max(
            0,
            Math.min(1, -(currentTime - downloadTime))
          );
          const mockupScale = Math.max(1, currentTime - downloadTime + 2);

          // Set animation values
          loginTextElement.style.opacity = `${textOpacity}`;
          loginTextElement.style.transform = `translate${axis}(${loginTextPos}rem)`;
          discoverTextElement.style.opacity = `${textOpacity}`;
          discoverTextElement.style.transform = `translate${axis}(${discoverTextPos}rem)`;
          matchesTextElement.style.opacity = `${textOpacity}`;
          matchesTextElement.style.transform = `translate${axis}(${matchesTextPos}rem)`;
          downloadTextElement.style.opacity = `${downloadTextOpacity}`;
          downloadTextElement.style.transform = `scale(${downloadTextScale})`;
          mockupVideoElement.currentTime = currentTime;
          mockupVideoElement.style.opacity = `${mockupOpacity}`;
          mockupVideoElement.style.transform = `scale(${mockupScale})`;
          mockupImageElement.style.opacity = `${mockupOpacity}`;
          mockupImageElement.style.transform = `scale(${mockupScale})`;
        }
      };
      window.addEventListener('scroll', handleAnimations);

      // Remove event listeners
      return () => {
        mockupVideoElement.removeEventListener(
          'loadedmetadata',
          handleScrollHeight
        );
        window.removeEventListener('resize', handleScrollHeight);
        window.removeEventListener('scroll', handleAnimations);
      };
    }
  }, []);

  const handleEmailNotifyFormSubmit = async (
    event: FormEvent<HTMLFormElement>
  ) => {
    event.preventDefault();
    if (emailInputElementRef?.current?.validity) {
      try {
        const response = await fetch(
          'https://europe-west3-students-tinder.cloudfunctions.net/https_onRequest_addNewLaunchNotification',
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({ email: emailInputElementRef.current.value }),
          }
        );
        const data = await response.json();
        const emailNotifyFormElement = emailNotifyFormElementRef.current;
        const formResponseElement = formResponseElementRef.current;
        const textElement = formResponseElement?.querySelector('*');
        if (
          data &&
          emailNotifyFormElement &&
          formResponseElement &&
          textElement
        ) {
          emailNotifyFormElement.style.display = 'none';
          textElement.textContent = data.message;
          formResponseElement.style.display = 'block';
          setTimeout(() => {
            emailNotifyFormElement.style.display = '';
            textElement.innerHTML = '';
            formResponseElement.style.display = '';
          }, 10000);
        }
        if (response.ok) {
          emailInputElementRef.current.value = '';
        }
      } catch (error) {
        //
      }
    }
  };

  return (
    <View
      scrollytellingElementRef={scrollytellingElementRef}
      loginTextElementRef={loginTextElementRef}
      discoverTextElementRef={discoverTextElementRef}
      matchesTextElementRef={matchesTextElementRef}
      mockupVideoElementRef={mockupVideoElementRef}
      mockupImageElementRef={mockupImageElementRef}
      downloadTextElementRef={downloadTextElementRef}
      emailNotifyFormElementRef={emailNotifyFormElementRef}
      emailInputElementRef={emailInputElementRef}
      formResponseElementRef={formResponseElementRef}
      onEmailNotifyFormSubmit={handleEmailNotifyFormSubmit}
    />
  );
};

export default Container;
